import { CustomsAssessmentMetadata } from 'types';

const CUSTOMS_FIELDS: (keyof CustomsAssessmentMetadata)[] = [
  'assessmentNoticeEntryType',
  'sodYear',
  'customsCode',
  'sodAssessmentSerial',
  'sodAssessmentNumber',
  'payersName',
];

export type CustomsMetadataResult =
  | { status: 'absent' }
  | { status: 'valid'; metadata: CustomsAssessmentMetadata }
  | { status: 'partial'; missing: (keyof CustomsAssessmentMetadata)[] };

const isSupplied = (value: unknown): boolean =>
  value !== undefined && value !== null && String(value).trim() !== '';

/**
 * Reads the customs assessment fields a merchant passed into the SDK. They
 * are all-or-nothing: some but not all is a merchant error, never an
 * ordinary payment.
 */
export const parseCustomsMetadata = (
  source: Partial<Record<keyof CustomsAssessmentMetadata, unknown>>,
): CustomsMetadataResult => {
  const missing = CUSTOMS_FIELDS.filter((field) => !isSupplied(source[field]));

  if (missing.length === CUSTOMS_FIELDS.length) {
    return { status: 'absent' };
  }

  if (missing.length > 0) {
    return { status: 'partial', missing };
  }

  const metadata = CUSTOMS_FIELDS.reduce(
    (fields, field) => ({ ...fields, [field]: String(source[field]).trim() }),
    {} as CustomsAssessmentMetadata,
  );

  return { status: 'valid', metadata };
};

/**
 * The assessment as one reference, e.g. NGAPP/2026/IM4/C/1234567.
 */
export const formatAssessmentReference = (
  metadata: CustomsAssessmentMetadata,
): string =>
  [
    metadata.customsCode,
    metadata.sodYear,
    metadata.assessmentNoticeEntryType,
    metadata.sodAssessmentSerial,
    metadata.sodAssessmentNumber,
  ].join('/');
